import Image from "next/image";
import parse, { DOMNode, domToReact, Element } from "html-react-parser";
import { useMemo } from "react";
import { coerceLocale, localizePath } from "@/utils/localize-path";

const parseStyle = (styleString: string): Record<string, string> => {
  return styleString.split(";").reduce(
    (acc, style) => {
      const [key, value] = style.split(":").map((s) => s.trim());
      if (key && value) {
        const camelCaseKey = key.replace(/-([a-z])/g, (_, letter) => letter.toUpperCase());
        acc[camelCaseKey] = value;
      }
      return acc;
    },
    {} as Record<string, string>,
  );
};

const HtmlWithImage = ({
  html,
  priority,
  lang,
}: {
  html: string;
  priority?: boolean;
  lang?: string;
}) => {
  const locale = coerceLocale(lang);
  const isBanner = useMemo(
    () => html.includes("banner-slide") || html.includes("banner-desktop"),
    [html],
  );
  const replace = (domNode: DOMNode) => {
    if (domNode instanceof Element) {
      if (domNode.name === "img" && domNode.attribs && domNode.attribs.src) {
        const imageW = domNode.attribs.width;
        const imageH = domNode.attribs.height;
        let inlineStyle = domNode.attribs.style ? parseStyle(domNode.attribs.style) : {};
        if (isBanner) {
          inlineStyle = {
            ...inlineStyle,
            objectFit: "fill",
            objectPosition: "right",
          };
        }
        return (
          <Image
            src={domNode.attribs.src}
            alt={domNode.attribs.alt || ""}
            className={domNode.attribs.class}
            id={domNode.attribs.id}
            layout={
              !domNode.parent || (!domNode.prev && !domNode.next) || isBanner
                ? "responsive"
                : undefined
            }
            priority={priority}
            width={imageW && !isNaN(Number(imageW)) ? Number(imageW) : 525}
            height={imageH ? Number(imageH) : 350}
            style={{
              ...inlineStyle,
            }}
          />
        );
      }

      if (domNode.name === "a" && domNode.attribs) {
        const href = domNode.attribs.href?.trim();
        const attributes = {
          ...domNode.attribs,
          // Only root-relative hrefs are localized; relative/query-only links keep
          // their document-relative resolution (consistent with localizeHtmlAnchors).
          ...(href?.startsWith("/") ? { href: localizePath(href, locale) } : {}),
          style: domNode.attribs.style ? parseStyle(domNode.attribs.style) : {},
        };
        return <a {...attributes}>{domToReact(domNode.children as DOMNode[], { replace })}</a>;
      }

      if (domNode.type === "tag") {
        return domToReact(domNode.children as DOMNode[], { replace });
      }
    }

    return undefined;
  };

  return (
    <>
      {parse(html, {
        replace,
      })}
    </>
  );
};

export default HtmlWithImage;
